import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Selector } from '@/components/ui/inputs/selector';
import { Spinner } from '@/components/ui/loading';
import { AreaPositionSelector } from './AreaPositionSelector';
import type {
  DimensionalFilterConfig,
  UseEmployeeDimensionalFilterResult,
} from '@/hooks/attendance/common/useEmployeeDimensionalFilter';


interface EmployeeDimensionalFilterProps {
  config: DimensionalFilterConfig;
  state: UseEmployeeDimensionalFilterResult;
  disabled?: boolean;
}

export const EmployeeDimensionalFilter: React.FC<EmployeeDimensionalFilterProps> = ({
  config,
  state,
  disabled = false,
}) => {
  const { t: translate } = useTranslation();
  const {
    dimensionalFilters, selectedEmployeeIds, isLoadingEmployees,
    isLoadingMoreEmployees, employeeHasMore, loadMoreEmployees,
    isLoadingPositions, catalogOptions, handlers,
  } = state;

  const employeeStatusOptions = useMemo(() => [
    { value: 'todos', label: translate('attendance:dimensionalFilter.statusAll') },
    { value: 'vigente', label: translate('attendance:dimensionalFilter.statusActive') },
    { value: 'no_vigente', label: translate('attendance:dimensionalFilter.statusInactive') },
  ], [translate]);

  const hasAnyFilter =
    config.company || config.area || config.position || config.costCenter ||
    config.location || config.contractModality || config.securityGroup ||
    config.employeeStatus || config.employee;

  if (!hasAnyFilter) return null;

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {config.company && (
        <Selector
          label={translate('attendance:dimensionalFilter.company')}
          value={dimensionalFilters.companyIds}
          options={catalogOptions.companyOptions}
          onChange={handlers.onCompanyChange}
          multiple
          searchable
          disabled={disabled}
          placeholder={translate('attendance:dimensionalFilter.allSelected')}
        />
      )}
      <AreaPositionSelector
        areaOptions={catalogOptions.areaOptions}
        positionOptions={catalogOptions.positionOptions}
        selectedAreaIds={dimensionalFilters.areaIds}
        selectedPositionIds={dimensionalFilters.positionIds}
        onAreaChange={handlers.onAreaChange}
        onPositionChange={handlers.onPositionChange}
        isLoadingPositions={isLoadingPositions}
        showArea={config.area}
        showPosition={config.position}
        disabled={disabled}
      />
      {config.costCenter && (
        <Selector
          label={translate('attendance:dimensionalFilter.costCenter')}
          value={dimensionalFilters.costCenterIds}
          options={catalogOptions.costCenterOptions}
          onChange={handlers.onCostCenterChange}
          multiple
          searchable
          disabled={disabled}
          placeholder={translate('attendance:dimensionalFilter.allSelected')}
        />
      )}
      {config.location && (
        <Selector
          label={translate('attendance:dimensionalFilter.location')}
          value={dimensionalFilters.locationIds}
          options={catalogOptions.locationOptions}
          onChange={handlers.onLocationChange}
          multiple
          searchable
          disabled={disabled}
          placeholder={translate('attendance:dimensionalFilter.allSelected')}
        />
      )}
      {config.securityGroup && (
        <Selector
          label={translate('attendance:dimensionalFilter.securityGroup')}
          value={dimensionalFilters.groupIds}
          options={catalogOptions.securityGroupOptions}
          onChange={handlers.onSecurityGroupChange}
          multiple
          searchable
          disabled={disabled}
          placeholder={translate('attendance:dimensionalFilter.allSelected')}
        />
      )}
      {config.contractModality && (
        <Selector
          label={translate('attendance:dimensionalFilter.contractModality')}
          value={dimensionalFilters.contractModalityIds}
          options={catalogOptions.contractModalityOptions}
          onChange={handlers.onContractModalityChange}
          multiple
          searchable
          disabled={disabled}
          placeholder={translate('attendance:dimensionalFilter.allSelected')}
        />
      )}
      {config.employeeStatus && (
        <Selector
          label={translate('attendance:dimensionalFilter.employeeStatus')}
          value={dimensionalFilters.status}
          options={employeeStatusOptions}
          onChange={handlers.onEmployeeStatusChange}
          disabled={disabled}
          placeholder={translate('attendance:dimensionalFilter.allSelected')}
        />
      )}
      {config.employee && (
        <div className="relative">
          {isLoadingEmployees && (
            <div className="absolute right-3 top-1/2 z-10 -translate-y-1/2">
              <Spinner size="sm" />
            </div>
          )}
          <Selector
            label={
              isLoadingEmployees
                ? translate('attendance:dimensionalFilter.updating')
                : translate('attendance:dimensionalFilter.employee')
            }
            value={selectedEmployeeIds.map(String)}
            options={catalogOptions.employeeOptions}
            onChange={handlers.onEmployeeSelectionChange}
            onSearchChange={handlers.onEmployeeSearchChange}
            onLoadMore={loadMoreEmployees}
            hasNextPage={employeeHasMore}
            isLoadingMore={isLoadingMoreEmployees}
            multiple
            searchable
            isLoading={isLoadingEmployees}
            disabled={disabled || isLoadingEmployees}
            placeholder={translate('attendance:dimensionalFilter.noneSelected')}
          />
        </div>
      )}
    </div>
  );
};
